import React, { useState, memo } from "react";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/outline";
import axios from "axios";
import { API_BACKEND_URL } from "../../../Config/Config";
import { toast } from "react-toastify";

import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { useUpdatePriorityTag } from "../../../useQuery";
import { useNotification } from "../../../hooks";

const EditableField = memo(({ value, onChange, multiline, type = "text", placeholder }) => {
  const Component = multiline ? "textarea" : "input";

  return (
    <Component
      type={type}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full p-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-apple bg-white transition-colors"
      rows={multiline ? 3 : undefined}
    />
  );
});

// Updated Modal component using MUI Dialog
const ContentModal = ({ isOpen, onClose, title, content }) => {
  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { maxHeight: "80vh" },
      }}
    >
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6" component="div">
          {title}
        </Typography>
        <IconButton onClick={onClose} sx={{ color: "grey.500" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <pre className="text-sm bg-gray-100 p-4 rounded overflow-auto whitespace-pre-wrap">
          {content}
        </pre>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const PriorityTagModal = ({ isOpen, onClose, testCase, onSave, isLoading }) => {
  const [priority, setPriority] = useState("");
  const [tags, setTags] = useState("");

  React.useEffect(() => {
    if (isOpen && testCase) {
      setPriority(testCase.priority || "");
      setTags(testCase.tags || "");
    }
  }, [isOpen, testCase]);

  const handleSave = () => {
    onSave({
      testCaseId: testCase.id,
      priority: priority.trim(),
      tags: tags.trim(),
    });
  };

  const handleClose = () => {
    setPriority("");
    setTags("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <h6 className="font-apple">Edit Priority & Tags</h6>
        <p className="font-apple font-medium text-sm">Test Case: {testCase?.testCase}</p>
      </DialogTitle>
      <DialogContent dividers>
        <div className="space-y-4">
          <div>
            <Typography variant="subtitle2" className="mb-2 font-medium font-apple">
              Priority
            </Typography>
            <EditableField
              value={priority}
              onChange={setPriority}
              type="text"
              placeholder="e.g., High, Medium, Low, Critical, etc."
            />
          </div>
          <div>
            <Typography variant="subtitle2" className="mb-2 font-medium font-apple">
              Tags
            </Typography>
            <EditableField
              value={tags}
              onChange={setTags}
              type="text"
              placeholder="e.g., urgent, api, backend, regression"
            />
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isLoading}
          className="bg-blue-600 hover:bg-blue-700 font-apple"
        >
          {isLoading ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const TableRow = memo(
  ({
    testCase,
    isSelected,
    onSelect,
    isGlobalEdit,
    editedData,
    onFieldChange,
    getTestResult,
    getMethodColor,
    formatJSONDisplay,
    onViewContent,
    onPriorityTagEdit,
  }) => {
    const result = getTestResult(testCase);

    const truncateText = (text, maxLength = 30) => {
      if (!text) return "Not specified";
      const str = typeof text === "string" ? text : JSON.stringify(text);
      return str.length > maxLength ? str.substring(0, maxLength) + "..." : str;
    };

    return (
      <tr className="hover:bg-gray-50 transition-colors group">
        <td className="w-12 px-2 py-4">
          <div className="flex items-center justify-center">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onSelect(testCase)}
              className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500 cursor-pointer"
            />
          </div>
        </td>
        <td className="w-16 px-2 py-4">
          <span className="text-sm font-apple text-gray-700 font-medium">#{testCase.id}</span>
        </td>
        <td className="w-40 px-2 py-4">
          {isGlobalEdit ? (
            <EditableField
              value={editedData?.endpoint || testCase.endpoint}
              onChange={(value) => onFieldChange(testCase.id, "endpoint", value)}
              multiline={true}
            />
          ) : (
            <Tooltip title={testCase.endpoint} arrow>
              <div className="text-sm font-apple text-gray-700">
                <code className="bg-gray-100 px-2 py-1 text-xs text-gray-800 block truncate">
                  {truncateText(testCase.endpoint, 25)}
                </code>
              </div>
            </Tooltip>
          )}
        </td>
        <td className="w-40 px-2 py-4">
          {isGlobalEdit ? (
            <EditableField
              value={editedData?.testCase || testCase.testCase}
              onChange={(value) => onFieldChange(testCase.id, "testCase", value)}
              multiline={true}
            />
          ) : (
            <Tooltip title={testCase.testCase} arrow>
              <div className="text-sm font-apple text-gray-700 font-medium truncate">
                {truncateText(testCase.testCase, 25)}
              </div>
            </Tooltip>
          )}
        </td>
        <td className="w-20 px-2 py-4">
          {isGlobalEdit ? (
            <FormControl size="small" fullWidth>
              <Select
                value={editedData?.httpMethod || testCase.httpMethod}
                onChange={(e) => onFieldChange(testCase.id, "httpMethod", e.target.value)}
                className="text-sm"
              >
                <MenuItem value="GET">GET</MenuItem>
                <MenuItem value="POST">POST</MenuItem>
                <MenuItem value="PUT">PUT</MenuItem>
                <MenuItem value="DELETE">DELETE</MenuItem>
              </Select>
            </FormControl>
          ) : (
            <span
              className={`inline-flex items-center px-2 py-1 text-xs font-semibold font-apple ${getMethodColor(testCase.httpMethod)}`}
            >
              {testCase.httpMethod}
            </span>
          )}
        </td>
        <td className="w-20 px-2 py-4">
          <span className="text-sm font-apple text-gray-700 font-medium">
            {testCase.expectedStatusCode}
          </span>
        </td>
        <td className="w-32 px-2 py-4">
          {isGlobalEdit ? (
            <EditableField
              value={editedData?.expectedResult || formatJSONDisplay(testCase.expectedResult)}
              onChange={(value) => onFieldChange(testCase.id, "expectedResult", value)}
              multiline={true}
            />
          ) : (
            <div className="flex items-center gap-1">
              <code className="bg-gray-50 px-2 py-1 text-xs text-gray-700 truncate flex-1">
                {truncateText(formatJSONDisplay(testCase.expectedResult), 20)}
              </code>
              <IconButton
                size="small"
                onClick={() =>
                  onViewContent("Expected Result", formatJSONDisplay(testCase.expectedResult))
                }
                className="p-1"
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </div>
          )}
        </td>
        <td className="w-20 px-2 py-4">
          <span
            className={`inline-flex items-center px-2 py-1 text-xs font-semibold font-apple ${
              testCase?.testType?.toLowerCase() === "positive"
                ? "bg-green-100 text-green-800"
                : "bg-red-100 text-red-800"
            }`}
          >
            {testCase?.testType?.substring(0, 3) || "N/A"}
          </span>
        </td>

        {/* Priority Cell */}
        <td className="w-24 px-2 py-4">
          <div className="flex items-center gap-1">
            <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded">
              {testCase.priority || "Not set"}
            </span>
            <IconButton
              size="small"
              onClick={() => onPriorityTagEdit(testCase)}
              className="opacity-0 group-hover:opacity-100 transition-opacity hover:bg-blue-50"
            >
              <EditIcon fontSize="small" className="text-blue-600" />
            </IconButton>
          </div>
        </td>

        {/* Tags Cell */}
        <td className="w-32 px-2 py-4">
          <div className="flex items-center gap-1">
            <div className="flex flex-wrap gap-1 flex-1">
              {testCase.tags && testCase.tags.trim() ? (
                testCase.tags
                  .split(",")
                  .slice(0, 2)
                  .map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded"
                    >
                      {tag.trim()}
                    </span>
                  ))
              ) : (
                <span className="text-xs text-gray-500">No tags</span>
              )}
              {testCase.tags && testCase.tags.split(",").length > 2 && (
                <span className="text-xs text-gray-500">
                  +{testCase.tags.split(",").length - 2} more
                </span>
              )}
            </div>
          </div>
        </td>

        <td className="w-24 px-2 py-4">
          {result ? (
            <span
              className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold font-apple ${
                result.success ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
              }`}
            >
              {result.success ? (
                <CheckCircleIcon className="h-3 w-3" />
              ) : (
                <XCircleIcon className="h-3 w-3" />
              )}
              {result.success ? "PASS" : "FAIL"}
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-1 text-xs font-medium font-apple border border-gray-300 text-gray-500 bg-gray-50">
              NOT RUN
            </span>
          )}
        </td>
        <td className="w-28 px-2 py-4">
          {isGlobalEdit ? (
            <EditableField
              value={editedData?.body || formatJSONDisplay(testCase.body)}
              onChange={(value) => onFieldChange(testCase.id, "body", value)}
              multiline={true}
            />
          ) : (
            <div className="flex items-center gap-1">
              <code className="bg-gray-50 px-2 py-1 text-xs text-gray-700 truncate flex-1">
                {truncateText(formatJSONDisplay(testCase.body), 15)}
              </code>
              <IconButton
                size="small"
                onClick={() => onViewContent("Body", formatJSONDisplay(testCase.body))}
                className="p-1"
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </div>
          )}
        </td>
        <td className="w-28 px-2 py-4">
          {isGlobalEdit ? (
            <EditableField
              value={editedData?.queryParams || formatJSONDisplay(testCase.queryParams)}
              onChange={(value) => onFieldChange(testCase.id, "queryParams", value)}
              multiline={true}
            />
          ) : (
            <div className="flex items-center gap-1">
              <code className="bg-gray-50 px-2 py-1 text-xs text-gray-700 truncate flex-1">
                {truncateText(formatJSONDisplay(testCase.queryParams), 15)}
              </code>
              <IconButton
                size="small"
                onClick={() =>
                  onViewContent("Query Params", formatJSONDisplay(testCase.queryParams))
                }
                className="p-1"
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </div>
          )}
        </td>
      </tr>
    );
  },
);

const TestCasesTable = ({
  testCases: initialTestCases,
  testResults,
  selectedTestCases = [],
  onTestCaseSelect,
  onMarkAsReviewed,
  onSaveTestCase,
  fetchAllTestCases,
  sortBy,
}) => {
  const [filters, setFilters] = useState({
    method: "",
  });
  // const [sortBy, setSortBy] = useState("");
  const [isGlobalEdit, setIsGlobalEdit] = useState(false);
  const [editedTestCases, setEditedTestCases] = useState({});
  const [modalState, setModalState] = useState({
    isOpen: false,
    title: "",
    content: "",
  });

  const [priorityTagModalState, setPriorityTagModalState] = useState({
    isOpen: false,
    testCase: null,
  });
  const notify = useNotification();

  const updatePriorityTagMutation = useUpdatePriorityTag({
    onSuccess: (data, variables) => {
      notify(`${data.message}`, "success");
      closePriorityTagModal();
      if (fetchAllTestCases) {
        fetchAllTestCases();
      }
    },
    onError: (error) => {
      notify("Failed to update priority and tags", "error");
      console.error("Update failed:", error);
    },
  });

  // Priority and tag handlers
  const handlePriorityTagEdit = (testCase) => {
    setPriorityTagModalState({
      isOpen: true,
      testCase: testCase,
    });
  };

  const handlePriorityTagSave = (data) => {
    updatePriorityTagMutation.mutate(data);
  };

  const closePriorityTagModal = () => {
    setPriorityTagModalState({
      isOpen: false,
      testCase: null,
    });
  };

  const parseTestCases = (data) => {
    try {
      if (typeof data === "string") {
        return JSON.parse(data);
      }
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error("Error parsing test cases:", error);
      return [];
    }
  };

  const testCases = parseTestCases(initialTestCases)
    .filter((testCase) => {
      return !filters.method || testCase.httpMethod === filters.method;
    })
    .sort((a, b) => {
      if (sortBy === "createdAt") {
        return new Date(a.createdAt) - new Date(b.createdAt);
      } else if (sortBy === "modifiedAt") {
        return new Date(a.modifiedAt) - new Date(b.modifiedAt);
      } else if (a.httpMethod === "GET" && b.httpMethod !== "GET") {
        return -1;
      } else if (a.httpMethod !== "GET" && b.httpMethod === "GET") {
        return 1;
      }
      return 0;
    });

  const getTestResult = (testCase) => {
    if (!testResults) return null;
    return testResults.find((result) => result.testCase === testCase.testCase);
  };

  const handleCheckboxChange = (testCase) => {
    if (onTestCaseSelect) {
      onTestCaseSelect(testCase);
    }
  };

  const handleFieldChange = (testCaseId, field, value) => {
    setEditedTestCases((prev) => ({
      ...prev,
      [testCaseId]: {
        ...prev[testCaseId],
        [field]: value,
      },
    }));
  };

  const handleViewContent = (title, content) => {
    setModalState({
      isOpen: true,
      title,
      content,
    });
  };

  const closeModal = () => {
    setModalState({
      isOpen: false,
      title: "",
      content: "",
    });
  };

  const handleSaveAll = async () => {
    const editedEntries = Object.entries(editedTestCases).filter(([testCaseId, data]) => {
      const originalTestCase = testCases.find((tc) => tc.id.toString() === testCaseId.toString());
      if (!originalTestCase) return false;

      return Object.entries(data).some(([key, value]) => {
        if (key === "body" || key === "queryParams") {
          const originalValue = formatJSONDisplay(originalTestCase[key]);
          return value !== originalValue;
        }
        return originalTestCase[key]?.toString() !== value?.toString();
      });
    });

    if (editedEntries.length === 0) {
      console.log("No changes detected");
      setIsGlobalEdit(false);
      setEditedTestCases({});
      return;
    }
    console.log("Saving edited test cases:", editedEntries);
    try {
      await axios.put(`${API_BACKEND_URL}/testcase/update`, editedEntries);
      notify("Test cases updated successfully", "success");
    } catch (error) {
      console.error("Error updating test case:", error);
    }

    setIsGlobalEdit(false);
    setEditedTestCases({});
  };

  const handleCancelClick = () => {
    setIsGlobalEdit(false);
    setEditedTestCases({});
  };

  const formatJSONDisplay = (data) => {
    if (!data) return "Not specified";
    try {
      return typeof data === "string" ? data : JSON.stringify(data, null, 2);
    } catch {
      return String(data);
    }
  };

  const getMethodColor = (method) => {
    switch (method) {
      case "GET":
        return "text-green-800 bg-green-100";
      case "POST":
        return "text-orange-800 bg-orange-100";
      case "PUT":
        return "text-blue-800 bg-blue-100";
      case "DELETE":
        return "text-red-800 bg-red-100";
      default:
        return "text-gray-800 bg-gray-100";
    }
  };

  if (!testCases.length) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-800 px-6 py-4 font-apple">
          <h3 className="font-semibold">No test cases available</h3>
          <p className="text-sm mt-1">No test cases data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-4 w-full font-apple">
      {/* Controls Row */}
      <div className="mb-6 flex justify-between items-center bg-white p-4 border border-gray-200">
        <div className="flex gap-3">
          {selectedTestCases.length > 0 && (
            <button
              onClick={() => onMarkAsReviewed(selectedTestCases)}
              className="bg-blue-600 text-white px-4 py-2 text-sm font-medium font-apple hover:bg-blue-700 transition-colors cursor-pointer"
            >
              Mark {selectedTestCases.length} as Reviewed
            </button>
          )}

          {isGlobalEdit ? (
            <>
              <button
                onClick={handleCancelClick}
                className="bg-gray-600 text-white px-4 py-2 text-sm font-medium font-apple hover:bg-gray-700 transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAll}
                className="bg-green-600 text-white px-4 py-2 text-sm font-medium font-apple hover:bg-green-700 transition-colors cursor-pointer"
              >
                Save All Changes
              </button>
            </>
          ) : (
            <button
              onClick={() => setIsGlobalEdit(true)}
              className="bg-blue-600 text-white px-4 py-2 text-sm font-medium font-apple hover:bg-blue-700 transition-colors cursor-pointer"
            >
              Edit Test Cases
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full table-fixed divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-12 px-2 py-4 text-left text-xs font-semibold font-apple text-gray-700 uppercase tracking-wider">
                  Select
                </th>
                <th className="w-16 px-2 py-4 text-left text-xs font-semibold font-apple text-gray-700 uppercase tracking-wider">
                  ID
                </th>
                <th className="w-40 px-2 py-4 text-left text-xs font-semibold font-apple text-gray-700 uppercase tracking-wider">
                  Endpoint
                </th>
                <th className="w-40 px-2 py-4 text-left text-xs font-semibold font-apple text-gray-700 uppercase tracking-wider">
                  Test Case
                </th>
                <th className="w-20 px-2 py-4 text-left text-xs font-semibold font-apple text-gray-700 uppercase tracking-wider">
                  Method
                </th>
                <th className="w-20 px-2 py-4 text-left text-xs font-semibold font-apple text-gray-700 uppercase tracking-wider">
                  Status
                </th>
                <th className="w-32 px-2 py-4 text-left text-xs font-semibold font-apple text-gray-700 uppercase tracking-wider">
                  Expected Result
                </th>
                <th className="w-20 px-2 py-4 text-left text-xs font-semibold font-apple text-gray-700 uppercase tracking-wider">
                  Type
                </th>
                <th className="w-24 px-2 py-4 text-left text-xs font-semibold font-apple text-gray-700 uppercase tracking-wider">
                  Priority
                </th>
                <th className="w-32 px-2 py-4 text-left text-xs font-semibold font-apple text-gray-700 uppercase tracking-wider">
                  Tags
                </th>
                <th className="w-24 px-2 py-4 text-left text-xs font-semibold font-apple text-gray-700 uppercase tracking-wider">
                  Result
                </th>
                <th className="w-28 px-2 py-4 text-left text-xs font-semibold font-apple text-gray-700 uppercase tracking-wider">
                  Body
                </th>
                <th className="w-28 px-2 py-4 text-left text-xs font-semibold font-apple text-gray-700 uppercase tracking-wider">
                  Query Params
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {testCases.map((testCase) => (
                <TableRow
                  key={testCase.id}
                  testCase={testCase}
                  isSelected={selectedTestCases.some((tc) => tc.id === testCase.id)}
                  onSelect={handleCheckboxChange}
                  isGlobalEdit={isGlobalEdit}
                  editedData={editedTestCases[testCase.id]}
                  onFieldChange={handleFieldChange}
                  getTestResult={getTestResult}
                  getMethodColor={getMethodColor}
                  formatJSONDisplay={formatJSONDisplay}
                  onViewContent={handleViewContent}
                  editingPriorityTag={null}
                  onPriorityTagEdit={() => handlePriorityTagEdit(testCase)}
                  onPriorityTagSave={null}
                  onPriorityTagCancel={null}
                  onPriorityTagChange={null}
                  isUpdatingPriorityTag={updatePriorityTagMutation.isPending}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Content Modal */}
      <ContentModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        title={modalState.title}
        content={modalState.content}
      />

      {/* Priority & Tags Modal */}
      <PriorityTagModal
        isOpen={priorityTagModalState.isOpen}
        onClose={closePriorityTagModal}
        testCase={priorityTagModalState.testCase}
        onSave={handlePriorityTagSave}
        isLoading={updatePriorityTagMutation.isPending}
      />
    </div>
  );
};

export default TestCasesTable;
